import React, { useState, useMemo } from 'react';
import { Search, MessageCircle, Bug, Home, Zap, FileText, BarChart3, Settings, Headphones, X, LayoutDashboard, ClipboardList, Droplet, Bell, Lock, MessageSquare } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

interface HelpSection {
  id: string;
  title: string;
  icon: React.ReactNode;
  description: string;
  content: React.ReactNode;
  searchContent: string;
}

interface ContactModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface BugReportModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ContactModal: React.FC<ContactModalProps> = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg max-w-md w-full mx-4 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Contact Support</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X size={20} />
          </button>
        </div>
        <p className="text-gray-600 mb-4">
          Our support team is ready to help you. Click the button below to start a chat with an agent.
        </p>
        <button
          className="w-full bg-cyan-600 text-white py-2 rounded-lg font-medium hover:bg-cyan-700 transition-colors"
          onClick={onClose}
        >
          Start Chat (Coming Soon)
        </button>
      </div>
    </div>
  );
};

const BugReportModal: React.FC<BugReportModalProps> = ({ isOpen, onClose }) => {
  // Call hooks unconditionally FIRST
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    steps: '',
    expectedBehavior: '',
    actualBehavior: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        ...formData,
        reportedBy: user?.email,
        timestamp: new Date().toISOString(),
      };
      console.log('Bug Report:', payload);

      setSubmitted(true);
      setTimeout(() => {
        setFormData({
          title: '',
          description: '',
          steps: '',
          expectedBehavior: '',
          actualBehavior: '',
        });
        setSubmitted(false);
        onClose();
      }, 2000);
    } catch (error) {
      console.error('Failed to submit bug report:', error);
    } finally {
      setLoading(false);
    }
  };

  // Safe to check isOpen here - it's after all hooks
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
      <div className="bg-white rounded-lg max-w-2xl w-full mx-4 my-8 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Report a Bug</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X size={20} />
          </button>
        </div>

        {submitted ? (
          <div className="text-center py-8">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
              <p className="text-green-800 font-medium">Thank you for reporting this bug!</p>
              <p className="text-green-700 text-sm mt-1">We&apos;ll review it and get back to you soon.</p>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Bug Title *
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder="e.g., Search button not working"
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description *
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Describe the bug in detail..."
                required
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Steps to Reproduce
              </label>
              <textarea
                name="steps"
                value={formData.steps}
                onChange={handleChange}
                placeholder="1. Click...\n2. Type...\n3. Observe..."
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Expected Behavior
                </label>
                <textarea
                  name="expectedBehavior"
                  value={formData.expectedBehavior}
                  onChange={handleChange}
                  placeholder="What should happen?"
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Actual Behavior
                </label>
                <textarea
                  name="actualBehavior"
                  value={formData.actualBehavior}
                  onChange={handleChange}
                  placeholder="What actually happens?"
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
                />
              </div>
            </div>

            <div className="flex gap-3 justify-end pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors disabled:opacity-50"
              >
                {loading ? 'Submitting...' : 'Submit Bug Report'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

const helpSections: HelpSection[] = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    icon: <Home size={20} />,
    description: 'Learn the basics of the Water Management System',
    searchContent: 'Getting started basics features dashboard requests reports updates notifications water management introduction',
    content: (
      <div className="space-y-4">
        <h3 className="text-xl font-semibold text-gray-900">Getting Started Guide</h3>
        <p className="text-gray-600">
          Welcome to the Water Management System call center portal. This guide will help you get started with the essential features and workflows.
        </p>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="font-semibold text-blue-900 mb-2">What is This System?</h4>
          <p className="text-blue-800 text-sm">
            The Water Management System is a comprehensive field service management platform designed to streamline water service operations. It connects call center agents with field technicians to manage service requests efficiently.
          </p>
        </div>

        <h4 className="font-semibold text-gray-900 mt-6">Key Features</h4>
        <ul className="space-y-2 text-gray-600">
          <li className="flex gap-2">
            <span className="text-cyan-600 font-semibold">•</span>
            <span><strong>Dashboard</strong>: Real-time overview of service requests and system health</span>
          </li>
          <li className="flex gap-2">
            <span className="text-cyan-600 font-semibold">•</span>
            <span><strong>Service Requests</strong>: Create, manage, and track all service tickets</span>
          </li>
          <li className="flex gap-2">
            <span className="text-cyan-600 font-semibold">•</span>
            <span><strong>Reports & Analytics</strong>: Generate insights about service performance</span>
          </li>
          <li className="flex gap-2">
            <span className="text-cyan-600 font-semibold">•</span>
            <span><strong>Real-time Updates</strong>: Instant notifications when statuses change</span>
          </li>
        </ul>

        <h4 className="font-semibold text-gray-900 mt-6">Quick Tips</h4>
        <ul className="space-y-2 text-gray-600 text-sm">
          <li>✓ Use the sidebar navigation to move between sections</li>
          <li>✓ The Dashboard shows all important metrics at a glance</li>
          <li>✓ Create requests quickly using the Create Request button</li>
          <li>✓ Search functionality is available throughout the app</li>
        </ul>
      </div>
    ),
  },
  {
    id: 'dashboard',
    title: 'Dashboard Guide',
    icon: <LayoutDashboard size={20} />,
    description: 'Understand the dashboard overview and statistics',
    searchContent: 'dashboard statistics metrics cards status open in progress pending completed active requests performance response time',
    content: (
      <div className="space-y-4">
        <h3 className="text-xl font-semibold text-gray-900">Dashboard Overview</h3>
        <p className="text-gray-600">
          The Dashboard provides a real-time snapshot of your service operations, including request statistics, performance metrics, and active requests.
        </p>

        <h4 className="font-semibold text-gray-900 mt-6">Key Sections</h4>

        <div className="space-y-4">
          <div className="border-l-4 border-cyan-600 pl-4">
            <h5 className="font-semibold text-gray-900">Status Cards</h5>
            <p className="text-gray-600 text-sm mt-1">
              View the count of requests in each status: Open, In Progress, Pending, and Completed. Click on any card to filter the Requests page by that status.
            </p>
          </div>

          <div className="border-l-4 border-cyan-600 pl-4">
            <h5 className="font-semibold text-gray-900">Performance Metrics</h5>
            <p className="text-gray-600 text-sm mt-1">
              Monitor average response time and completion time to track team performance. Lower times indicate better efficiency.
            </p>
          </div>

          <div className="border-l-4 border-cyan-600 pl-4">
            <h5 className="font-semibold text-gray-900">Active Requests</h5>
            <p className="text-gray-600 text-sm mt-1">
              See all open and in-progress requests at a glance. Click on any request to view full details and make updates.
            </p>
          </div>

          <div className="border-l-4 border-cyan-600 pl-4">
            <h5 className="font-semibold text-gray-900">Auto-Refresh</h5>
            <p className="text-gray-600 text-sm mt-1">
              The dashboard automatically updates every 10 seconds to show the latest information. No manual refresh needed.
            </p>
          </div>
        </div>

        <h4 className="font-semibold text-gray-900 mt-6">Tips</h4>
        <ul className="space-y-2 text-gray-600 text-sm">
          <li>✓ Click status cards to filter and drill down</li>
          <li>✓ Use the mute button to silence notifications</li>
          <li>✓ Check metrics regularly to monitor team performance</li>
        </ul>
      </div>
    ),
  },
  {
    id: 'requests',
    title: 'Service Requests',
    icon: <ClipboardList size={20} />,
    description: 'Create and manage service requests',
    searchContent: 'service requests create manage tickets filter search ticket number title description priority category location branch technician',
    content: (
      <div className="space-y-4">
        <h3 className="text-xl font-semibold text-gray-900">Service Requests Guide</h3>
        <p className="text-gray-600">
          Service requests are the core of the system. Learn how to create, update, and manage tickets efficiently.
        </p>

        <h4 className="font-semibold text-gray-900 mt-6">Creating a Request</h4>
        <div className="bg-gray-50 rounded-lg p-4 space-y-3 text-sm text-gray-700">
          <p><strong>1. Navigate to Create Request</strong> - Use the sidebar menu</p>
          <p><strong>2. Fill in Details:</strong></p>
          <ul className="ml-4 space-y-2">
            <li>• <strong>Title</strong>: Brief description of the issue</li>
            <li>• <strong>Description</strong>: Detailed explanation</li>
            <li>• <strong>Priority</strong>: Urgent, High, Medium, or Low</li>
            <li>• <strong>Category</strong>: Type of water issue</li>
            <li>• <strong>Location</strong>: Service address</li>
            <li>• <strong>Branch</strong>: Assign to service area</li>
          </ul>
          <p><strong>3. Submit</strong> - Click &quot;Create Request&quot; to post</p>
        </div>

        <h4 className="font-semibold text-gray-900 mt-6">Request Status Workflow</h4>
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            <div className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-xs font-semibold">Open</div>
            <span className="text-gray-600">New request, waiting for technician</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-xs font-semibold">In Progress</div>
            <span className="text-gray-600">Technician actively working</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="bg-orange-100 text-orange-800 px-3 py-1 rounded-full text-xs font-semibold">Pending</div>
            <span className="text-gray-600">Waiting for parts or resources</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-xs font-semibold">Completed</div>
            <span className="text-gray-600">Work finished and verified</span>
          </div>
        </div>

        <h4 className="font-semibold text-gray-900 mt-6">Managing Requests</h4>
        <ul className="space-y-2 text-gray-600 text-sm">
          <li>✓ Click on any request to view full details</li>
          <li>✓ Update status as work progresses</li>
          <li>✓ Add notes and comments for communication</li>
          <li>✓ Filter requests by status or branch</li>
          <li>✓ Search by ticket number or description</li>
        </ul>
      </div>
    ),
  },
  {
    id: 'request-details',
    title: 'Request Details',
    icon: <FileText size={20} />,
    description: 'Understanding the request details page',
    searchContent: 'request details page information ticket number title description priority status category leakage materials comments activity history field changes',
    content: (
      <div className="space-y-4">
        <h3 className="text-xl font-semibold text-gray-900">Request Details Page</h3>
        <p className="text-gray-600">
          The request details page shows comprehensive information about a service request and allows you to make updates.
        </p>

        <h4 className="font-semibold text-gray-900 mt-6">Key Information Sections</h4>
        <div className="space-y-3">
          <div className="border-l-4 border-cyan-600 pl-4">
            <h5 className="font-semibold text-gray-900">Basic Information</h5>
            <p className="text-gray-600 text-sm mt-1">Ticket number, title, description, priority, and status</p>
          </div>

          <div className="border-l-4 border-cyan-600 pl-4">
            <h5 className="font-semibold text-gray-900">Service Details</h5>
            <p className="text-gray-600 text-sm mt-1">Category (water issue type), leakage information, materials used</p>
          </div>

          <div className="border-l-4 border-cyan-600 pl-4">
            <h5 className="font-semibold text-gray-900">Status Management</h5>
            <p className="text-gray-600 text-sm mt-1">Change request status with optional notes about the transition</p>
          </div>

          <div className="border-l-4 border-cyan-600 pl-4">
            <h5 className="font-semibold text-gray-900">Comments & Activity</h5>
            <p className="text-gray-600 text-sm mt-1">Add comments and view complete activity history with timestamps</p>
          </div>
        </div>

        <h4 className="font-semibold text-gray-900 mt-6">Updating Request Details</h4>
        <ol className="space-y-2 text-gray-600 text-sm list-decimal list-inside">
          <li>Change the status from the status dropdown</li>
          <li>Update category, leakage type, or materials as needed</li>
          <li>Add comments to document progress</li>
          <li>All changes are automatically saved and logged</li>
        </ol>

        <h4 className="font-semibold text-gray-900 mt-6">Activity Tracking</h4>
        <p className="text-gray-600 text-sm">
          Every change to a request is tracked in the activity log, including status changes, field updates, and comments. This creates a complete audit trail for each service request.
        </p>
      </div>
    ),
  },
  {
    id: 'notifications',
    title: 'Notifications',
    icon: <Bell size={20} />,
    description: 'How to manage notifications and stay updated',
    searchContent: 'notifications alerts updates new requests status changes real-time mute unmute sound browser settings preferences',
    content: (
      <div className="space-y-4">
        <h3 className="text-xl font-semibold text-gray-900">Notifications Guide</h3>
        <p className="text-gray-600">
          Stay informed with real-time notifications for new service requests and status updates.
        </p>

        <h4 className="font-semibold text-gray-900 mt-6">Types of Notifications</h4>
        <div className="space-y-3">
          <div className="bg-blue-50 rounded-lg p-4">
            <h5 className="font-semibold text-blue-900">New Request Notifications</h5>
            <p className="text-blue-800 text-sm mt-2">Receive alerts when new service requests are created. Shows ticket number, title, and location.</p>
          </div>

          <div className="bg-cyan-50 rounded-lg p-4">
            <h5 className="font-semibold text-cyan-900">Status Change Notifications</h5>
            <p className="text-cyan-800 text-sm mt-2">Get notified when request status changes. Shows the transition (e.g., Open → In Progress).</p>
          </div>

          <div className="bg-purple-50 rounded-lg p-4">
            <h5 className="font-semibold text-purple-900">Real-time Updates</h5>
            <p className="text-purple-800 text-sm mt-2">Dashboard and Requests page auto-refresh every 10 seconds to show latest data.</p>
          </div>
        </div>

        <h4 className="font-semibold text-gray-900 mt-6">Managing Notifications</h4>
        <ul className="space-y-2 text-gray-600 text-sm">
          <li>✓ Click the mute button (top-right) to disable notification sounds temporarily</li>
          <li>✓ Mute settings persist when you refresh or switch pages</li>
          <li>✓ Notifications will still appear as toast messages even when muted</li>
          <li>✓ Visual notifications always show regardless of mute status</li>
          <li>✓ Browser notifications may require permission - check your browser settings</li>
        </ul>

        <h4 className="font-semibold text-gray-900 mt-6">Notification Settings</h4>
        <p className="text-gray-600 text-sm">
          Notification settings are stored in your browser. Clear browser data or use private/incognito mode will reset your mute preference.
        </p>
      </div>
    ),
  },
  {
    id: 'reports',
    title: 'Reports & Analytics',
    icon: <BarChart3 size={20} />,
    description: 'Generate reports and analyze performance',
    searchContent: 'reports analytics performance metrics charts graphs status distribution priority response time category breakdown trends data export',
    content: (
      <div className="space-y-4">
        <h3 className="text-xl font-semibold text-gray-900">Reports & Analytics</h3>
        <p className="text-gray-600">
          Use reports to analyze service performance, identify trends, and make data-driven decisions.
        </p>

        <h4 className="font-semibold text-gray-900 mt-6">Available Reports</h4>
        <div className="space-y-3">
          <div className="bg-gray-50 rounded-lg p-4">
            <h5 className="font-semibold text-gray-900">Status Distribution</h5>
            <p className="text-gray-600 text-sm mt-1">See the breakdown of requests by status (Open, In Progress, Pending, Completed)</p>
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            <h5 className="font-semibold text-gray-900">Priority Analysis</h5>
            <p className="text-gray-600 text-sm mt-1">Understand how requests are distributed across priority levels</p>
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            <h5 className="font-semibold text-gray-900">Response Time Metrics</h5>
            <p className="text-gray-600 text-sm mt-1">Track average time to respond to requests and identify bottlenecks</p>
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            <h5 className="font-semibold text-gray-900">Category Breakdown</h5>
            <p className="text-gray-600 text-sm mt-1">See which types of water issues are most common</p>
          </div>
        </div>

        <h4 className="font-semibold text-gray-900 mt-6">Using Reports</h4>
        <ul className="space-y-2 text-gray-600 text-sm">
          <li>✓ Review reports regularly to monitor trends</li>
          <li>✓ Identify peak request times and resource needs</li>
          <li>✓ Track team performance metrics</li>
          <li>✓ Use data to improve processes and efficiency</li>
          <li>✓ Export reports for stakeholder meetings (coming soon)</li>
        </ul>
      </div>
    ),
  },
  {
    id: 'account-settings',
    title: 'Account & Settings',
    icon: <Lock size={20} />,
    description: 'Manage your profile and account settings',
    searchContent: 'account settings profile user information email password logout company details branch management role permissions access control',
    content: (
      <div className="space-y-4">
        <h3 className="text-xl font-semibold text-gray-900">Account & Settings</h3>
        <p className="text-gray-600">
          Manage your profile, account information, and system preferences.
        </p>

        <h4 className="font-semibold text-gray-900 mt-6">Profile Information</h4>
        <ul className="space-y-2 text-gray-600 text-sm">
          <li>✓ View your user name and email address</li>
          <li>✓ See your assigned role and permissions</li>
          <li>✓ View branches you have access to</li>
          <li>✓ Company information and details</li>
        </ul>

        <h4 className="font-semibold text-gray-900 mt-6">Access Control</h4>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h5 className="font-semibold text-blue-900 mb-2">User Roles</h5>
          <ul className="space-y-2 text-blue-800 text-sm">
            <li><strong>Company Owner</strong>: Full control, can manage team, branches, and settings</li>
            <li><strong>Manager</strong>: Can manage requests, technicians, and branches</li>
            <li><strong>Technician</strong>: Can view and update assigned requests</li>
            <li><strong>Operator</strong>: Limited access, can create requests</li>
          </ul>
        </div>

        <h4 className="font-semibold text-gray-900 mt-6">Logout</h4>
        <p className="text-gray-600 text-sm">
          Click the &quot;Sign Out&quot; button in the sidebar to securely log out. This will end your session and return you to the login screen.
        </p>

        <h4 className="font-semibold text-gray-900 mt-6">Security Tips</h4>
        <ul className="space-y-2 text-gray-600 text-sm">
          <li>✓ Log out when finished working, especially on shared computers</li>
          <li>✓ Never share your login credentials with others</li>
          <li>✓ Use a strong, unique password for your account</li>
          <li>✓ Ensure your browser is up to date for security</li>
        </ul>
      </div>
    ),
  },
  {
    id: 'best-practices',
    title: 'Best Practices',
    icon: <Zap size={20} />,
    description: 'Tips for efficient operations',
    searchContent: 'best practices efficiency guidelines tips communication documentation field service management quality standards procedures workflow optimization',
    content: (
      <div className="space-y-4">
        <h3 className="text-xl font-semibold text-gray-900">Best Practices</h3>
        <p className="text-gray-600">
          Follow these guidelines to maximize efficiency and ensure quality service.
        </p>

        <h4 className="font-semibold text-gray-900 mt-6">Request Creation</h4>
        <ul className="space-y-2 text-gray-600 text-sm">
          <li>✓ Use clear, descriptive titles that summarize the issue</li>
          <li>✓ Provide detailed descriptions with location information</li>
          <li>✓ Assign appropriate priority levels based on urgency</li>
          <li>✓ Select the correct category for better routing</li>
          <li>✓ Always assign to the correct service branch</li>
        </ul>

        <h4 className="font-semibold text-gray-900 mt-6">Status Management</h4>
        <ul className="space-y-2 text-gray-600 text-sm">
          <li>✓ Update status as soon as work begins</li>
          <li>✓ Use status transitions to communicate with field teams</li>
          <li>✓ Keep status &quot;Pending&quot; only when waiting for external factors</li>
          <li>✓ Mark complete only when work is fully finished</li>
          <li>✓ Add notes when changing status for clarity</li>
        </ul>

        <h4 className="font-semibold text-gray-900 mt-6">Communication</h4>
        <ul className="space-y-2 text-gray-600 text-sm">
          <li>✓ Add detailed comments to keep everyone informed</li>
          <li>✓ Include reference numbers when relevant</li>
          <li>✓ Respond promptly to requests and comments</li>
          <li>✓ Use the system as a single source of truth</li>
          <li>✓ Document all important decisions and changes</li>
        </ul>

        <h4 className="font-semibold text-gray-900 mt-6">Data Quality & Documentation</h4>
        <ul className="space-y-2 text-gray-600 text-sm">
          <li>✓ Keep all request information accurate and up-to-date</li>
          <li>✓ Document materials used and work performed</li>
          <li>✓ Record all service times and completion dates</li>
          <li>✓ Include relevant details for future reference</li>
        </ul>

        <h4 className="font-semibold text-gray-900 mt-6">Analytics & Performance</h4>
        <ul className="space-y-2 text-gray-600 text-sm">
          <li>✓ Review dashboards weekly for trends</li>
          <li>✓ Use reports to identify improvement opportunities</li>
          <li>✓ Monitor response times and set improvement goals</li>
          <li>✓ Share insights with your team regularly</li>
        </ul>
      </div>
    ),
  },
];

export default function Help() {
  const [selectedSection, setSelectedSection] = useState<string>(helpSections[0]?.id || 'getting-started');
  const [searchQuery, setSearchQuery] = useState('');
  const [showContactModal, setShowContactModal] = useState(false);
  const [showBugReportModal, setShowBugReportModal] = useState(false);

  // Filter sections based on search across title, description, and content
  const filteredSections = useMemo(() => {
    if (!searchQuery.trim()) return helpSections;

    const query = searchQuery.toLowerCase();
    return helpSections.filter(
      section =>
        section.title.toLowerCase().includes(query) ||
        section.description.toLowerCase().includes(query) ||
        section.searchContent.toLowerCase().includes(query)
    );
  }, [searchQuery]);

  const selectedContent = helpSections.find(s => s.id === selectedSection);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header with branding */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="px-6 py-6">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div className="flex items-start gap-3">
              <Droplet size={40} className="text-cyan-600 flex-shrink-0 pt-0.5" />
              <div>
                <h1 className="text-3xl font-bold text-gray-900 leading-none">AquaFlow</h1>
                <p className="text-sm font-medium text-gray-500 leading-tight">Help Center</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {/* Search bar */}
              <div className="relative">
                <Search className="absolute left-3 top-3 text-gray-400" size={18} />
                <input
                  type="text"
                  placeholder="Search help..."
                  value={searchQuery}
                  onChange={(e: any) => setSearchQuery(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 w-64"
                />
              </div>

              {/* Contact and Bug Report buttons */}
              <button
                onClick={() => setShowContactModal(true)}
                className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                title="Contact support team"
              >
                <MessageCircle size={18} />
                <span className="hidden sm:inline">Contact Us</span>
              </button>

              <button
                onClick={() => setShowBugReportModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-red-50 text-red-700 hover:bg-red-100 rounded-lg transition-colors"
                title="Report a bug"
              >
                <Bug size={18} />
                <span className="hidden sm:inline">Bug Report</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="flex h-[calc(100vh-120px)]">
        {/* Help-specific sidebar navigation */}
        <div className="w-64 bg-white border-r border-gray-200 overflow-y-auto">
          <div className="p-4 space-y-1">
            {filteredSections.map((section) => (
              <button
                key={section.id}
                onClick={() => setSelectedSection(section.id)}
                className={`w-full flex items-start gap-3 px-4 py-3 rounded-lg text-left transition-colors ${
                  selectedSection === section.id
                    ? 'bg-cyan-50 text-cyan-700'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <div className="mt-0.5">{section.icon}</div>
                <div>
                  <div className="font-medium text-sm">{section.title}</div>
                  <div className={`text-xs ${selectedSection === section.id ? 'text-cyan-600' : 'text-gray-500'}`}>
                    {section.description}
                  </div>
                </div>
              </button>
            ))}

            {filteredSections.length === 0 && (
              <div className="px-4 py-8 text-center text-gray-500">
                <p className="text-sm">No results found for &quot;{searchQuery}&quot;</p>
              </div>
            )}
          </div>
        </div>

        {/* Main content area */}
        <div className="flex-1 overflow-y-auto">
          {selectedContent ? (
            <div className="max-w-4xl mx-auto px-8 py-8">
              {selectedContent.content}
            </div>
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="text-center text-gray-500">
                <p>Select a topic from the sidebar to get started</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <ContactModal isOpen={showContactModal} onClose={() => setShowContactModal(false)} />
      <BugReportModal isOpen={showBugReportModal} onClose={() => setShowBugReportModal(false)} />
    </div>
  );
}
